import {
  Component,
  Input,
  HostListener,
  signal,
  inject,
  PLATFORM_ID,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';

@Component({
  selector: 'app-scroll-to-top',
  standalone: true,
  imports: [CommonModule],
  template: `
    <button
      class="scroll-to-top"
      [class.show]="isVisible()"
      [class.glass]="theme === 'glass'"
      [class.minimal]="theme === 'minimal'"
      (click)="scrollToTop()"
      aria-label="Scroll to top"
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <polyline points="18 15 12 9 6 15"></polyline>
      </svg>
    </button>
  `,
  styles: [
    `
      .scroll-to-top {
        position: fixed;
        bottom: 2rem;
        right: 2rem;
        width: 50px;
        height: 50px;
        border-radius: 50%;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0;
        visibility: hidden;
        transform: translateY(20px) scale(0.8);
        transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        z-index: 9999;
        box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
      }

      .scroll-to-top.show {
        opacity: 1;
        visibility: visible;
        transform: translateY(0) scale(1);
      }

      /* Liquid Glass Theme */
      .scroll-to-top.glass {
        background: rgba(255, 255, 255, 0.1);
        backdrop-filter: blur(12px);
        -webkit-backdrop-filter: blur(12px);
        border: 1px solid rgba(197, 164, 126, 0.3); /* Match --color-accent-light at low opacity */
        color: #c5a47e; /* --color-accent */

        &:hover {
          background: rgba(197, 164, 126, 0.2);
          border-color: #c5a47e;
          transform: translateY(-5px) scale(1.05);
          box-shadow: 0 15px 30px rgba(197, 164, 126, 0.3);
        }

        svg {
          width: 24px;
          height: 24px;
          filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
        }
      }

      /* Minimal Theme */
      .scroll-to-top.minimal {
        background: #0a192f; /* --color-primary */
        border: 1px solid rgba(255, 255, 255, 0.1);
        color: rgba(255, 255, 255, 0.9);
        width: 40px;
        height: 40px;
        bottom: 1.5rem;
        right: 1.5rem;

        &:hover {
          background: #112240;
          color: #fff;
          transform: translateY(-3px);
        }

        svg {
          width: 20px;
          height: 20px;
        }
      }

      @media (max-width: 768px) {
        .scroll-to-top {
          bottom: 1.5rem;
          right: 1.5rem;
        }
      }
    `,
  ],
})
export class ScrollToTopComponent {
  @Input() theme: 'glass' | 'minimal' = 'minimal';
  @Input() targetContainerSelector?: string;

  private platformId = inject(PLATFORM_ID);
  isVisible = signal(false);
  private containerElement?: HTMLElement;

  ngOnInit() {
    if (isPlatformBrowser(this.platformId) && this.targetContainerSelector) {
      const selector = this.targetContainerSelector;
      setTimeout(() => {
        this.containerElement = document.querySelector(selector) as HTMLElement;
        if (this.containerElement) {
          this.containerElement.addEventListener('scroll', () => this.checkScroll());
        }
      }, 500);
    }
  }

  @HostListener('window:scroll', [])
  onWindowScroll() {
    if (!this.targetContainerSelector) {
      this.checkScroll();
    }
  }

  checkScroll() {
    if (isPlatformBrowser(this.platformId)) {
      const scrollPos =
        this.targetContainerSelector && this.containerElement
          ? this.containerElement.scrollTop
          : (typeof window !== 'undefined'
              ? (window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0)
              : 0);

      this.isVisible.set(scrollPos > 300);
    }
  }

  scrollToTop() {
    if (isPlatformBrowser(this.platformId)) {
      if (this.targetContainerSelector && this.containerElement) {
        this.containerElement.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  }
}
